import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HeartHandshake } from 'lucide-react'
import { AskEyebrow } from './AskEyebrow'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'
import {
  answerQuietDaysCheckin, type QuietDaysAnswer, type QuietDaysCheckin,
} from '../../services/withYouService'

/**
 * The one check-in Horton sends when you have gone quiet (2026-09-15).
 *
 * Owner: *"I am not thrown any window from horton checking if everything is
 * okay … to see if any workload needs adjustments and if there is any
 * priority that has come up."* saas-core decides WHEN (≥2 quiet local days
 * after an active stretch, once a week at most, in your daytime); the
 * motivator pushes it with a deep link here; this card takes the answer.
 *
 * Three answers, one tap each. "Lighten today" lowers today's ceiling (the
 * rationing readiness Auto-Mom reads) until the end of your day; "Something
 * new came up" hands you to capture; "All good" records it and leaves you be.
 * Words: quiet days, never inactive; nothing is behind.
 */
const ANSWERS: Array<{ key: QuietDaysAnswer; label: string }> = [
  { key: 'lighten', label: 'Lighten today' },
  { key: 'new_priority', label: 'Something new came up' },
  { key: 'all_good', label: 'All good' },
]

export function QuietDaysCheckinCard({
  checkin, onAnswered,
}: { checkin: QuietDaysCheckin | null; onAnswered: () => void }) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState<QuietDaysAnswer | null>(null)
  const [reply, setReply] = useState<string | null>(null)
  if (!checkin) return null

  const days = checkin.quiet_days <= 2 ? 'a couple of quiet days' : `${checkin.quiet_days} quiet days`

  const answer = async (key: QuietDaysAnswer) => {
    if (busy) return
    setBusy(key)
    try {
      const { words } = await answerQuietDaysCheckin(checkin.id, key)
      setReply(words)
      if (key === 'new_priority') {
        navigate('/tasks/create')
        return
      }
      window.setTimeout(onAnswered, 2400)
    } catch {
      setReply('That did not go through. Try once more?')
      setBusy(null)
    }
  }

  return (
    <div
      className="rounded-2xl border border-brand-subtle-border bg-gradient-to-br from-brand-subtle to-surface p-4 shadow-sm"
      data-testid="quiet-days-checkin"
    >
      <AskEyebrow icon={<HeartHandshake />} label={ORI_KIND_LABEL.decide} typeWord="checking in" />
      {reply ? (
        <p className="mt-2 text-app-body text-primary">{reply}</p>
      ) : (
        <>
          <p className="mt-2 text-app-body text-primary">
            It&rsquo;s been {days} on your side. Everything okay? Nothing is behind &mdash; I just want today to fit you.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {ANSWERS.map(a => (
              <button
                key={a.key}
                type="button"
                disabled={busy !== null}
                onClick={() => void answer(a.key)}
                className="rounded-full border border-brand px-3 py-1.5 text-sm font-medium text-brand hover:bg-brand-subtle disabled:opacity-50"
              >
                {busy === a.key ? '…' : a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default QuietDaysCheckinCard
